import { searchApi } from '@/core/search/export';
import marketSignalApi from '@/modules/market-signals/api/market-signal-api';
import { Loading } from '@/shared/components/export';
import { Menu } from '@/core/menu/export';
import { PageControl } from '@/shared/pagination/export';
import { FormEvent, useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalHeader } from 'react-bootstrap';
import { MarketSignal } from '@/modules/market-signals/model/market-signals/market-signal';
import MarketSignalScrollerEntry from '../scroller/market-signal-scroller-entry';

export default function MarketSignalSearchModal({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState<boolean>(false);
  const [lastQuery, setLastQuery] = useState<string>('');
  const [query, setQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [sourceFilter, setSourceFilter] = useState<string>();
  const [platformFilter, setPlatformFilter] = useState<string>();
  const [signals, setSignals] = useState<MarketSignal[]>();

  const handleClose = () => {
    setQuery('');
    setLastQuery('');
    setSourceFilter(undefined);
    setPlatformFilter(undefined);
    setSignals(undefined);
    onClose();
  };

  useEffect(() => {
    if (query.trim() === '') {
      setSignals(undefined);
    } else {
      search(query, page);
    }
    // eslint-disable-next-line
  }, [page]);

  const doSearch = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!query) return setSignals(undefined);

    setPage(0);
    search(query, page);
  };

  const search = async (searchQuery: string, page: number) => {
    setLoading(true);
    setSignals(undefined);

    try {
      const response = await searchApi.search({
        // page + 1 so PageControl works properly
        pagination: { page: page + 1, pageSize: 10 },
        filters: {
          sortBy: 'date:desc',
          types: ['signal'],
          ...(searchQuery ? { search: searchQuery } : {}),
          filters: [
            ...(sourceFilter ? [{ key: 'infoSource', value: sourceFilter }] : []),
            ...(platformFilter ? [{ key: 'sourcePlatform', value: platformFilter }] : []),
          ],
        },
      });

      if (!response.results || response.results.length === 0) {
        setSignals([]);
        return;
      }

      const signalUris = response.results.map(r => decodeURIComponent(r.uri.split(':')[1]));
      const { content } = await marketSignalApi.getSignalsByIds(signalUris);

      setSignals(content);
      setLastQuery(searchQuery);
    } catch (e) {
      console.error(`Signal search failed: ${e}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} size={'lg'} onHide={handleClose}>
      <ModalHeader>
        <div className={'d-flex w-100 justify-content-between align-items-center'}>
          <div className={'h3'}>Market Signal Search</div>
          <Button onClick={handleClose} variant={'warning'}>
            Close
          </Button>
        </div>
      </ModalHeader>

      <ModalBody>
        <div>
          <form onSubmit={event => doSearch(event)}>
            <div className="input-group input-group-sm mb-2">
              <input
                type="text"
                className="form-control"
                placeholder="enter search terms or '*'"
                aria-label="enter search terms or '*'"
                aria-describedby="button-addon2"
                onChange={event => setQuery(event.target.value)}
              />
              <input
                type={'submit'}
                className="btn btn-sm btn-outline-secondary"
                id="button-addon2"
                value={'Search'}
              />
              <Menu />
            </div>

            <div className="input-group input-group-sm mb-2">
              <input
                type="text"
                className="form-control"
                placeholder="filter by info source"
                aria-label="filter by info source"
                onChange={event => setSourceFilter(event.target.value)}
              />
              <select
                className="form-control"
                onChange={event => setPlatformFilter(event.target.value)}
              >
                <option value="">{'<All Platforms>'}</option>
                <option value="truth-social">truth-social</option>
                <option value="twitter/x">twitter/x</option>
                <option value="website">website</option>
                <option value="pipeline-notification">pipeline-notification</option>
                <option value="linkedin">linkedin</option>
              </select>
            </div>
          </form>
        </div>

        {signals && signals.length > 0 && (
          <div className={'mb-3'}>
            <PageControl offset={page} onChange={setPage} menu={<Menu />} />
          </div>
        )}

        <div className={'flex-fill overflow-auto'}>
          {loading && (
            <div>
              <Loading />
            </div>
          )}
          {signals && signals.length > 0 && (
            <div className={'h-100 container-fluid'}>
              {signals?.map(value => {
                return <MarketSignalScrollerEntry signal={value} />;
              })}
            </div>
          )}
          {!loading && signals && signals.length === 0 && (
            <div className={'text-muted h4 text-center p-5'}>Nothing found for: {lastQuery}</div>
          )}
          {!loading && !signals && (
            <div className={'text-muted h4 text-center p-5'}>Enter search terms above</div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
}
